/* eslint-disable no-unused-vars */
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as S from './ChooseApplications.styled';
import {
  ModalBase,
  ModalHeader,
  ModalFooter,
  ChooseBase,
  Buttons,
  ErrorComponent,
  ContentWrapp,
  Loader,
} from 'components';
import { useDebounce } from 'utils';
import { useToggleState } from './useToggleState';
import { Content } from './Content';
import { isAdvertiserRole } from 'store/selectors';
import { searchExcludeAPI } from 'services/searchExcludeAPI.service';

const ChooseApplications = ({ title, selectedApplications, setSelectedApplications }) => {
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const debouncedValue = useDebounce(search, 1500);
  const isAdvertiser = useSelector(isAdvertiserRole);

  const [
    publisherTrigger,
    { data: foundedPubApps, isError: isPubError, isFetching: isPubFetching },
  ] = searchExcludeAPI.useLazyFetchSerachApplicationsQuery();

  const [
    advertiserTrigger,
    { data: foundedAdvApps, isError: isAdvError, isFetching: isAdvFetching },
  ] = searchExcludeAPI.useLazyFetchSearchPublisherApplicationsQuery();

  const foundedApps = useMemo(() => {
    if (isAdvertiser) return foundedAdvApps;
    if (!isAdvertiser) return foundedPubApps;
  }, [foundedPubApps, foundedAdvApps]);

  const isFetching = useMemo(() => {
    if (isAdvertiser) return isAdvFetching;
    if (!isAdvertiser) return isPubFetching;
  }, [isPubFetching, isAdvFetching]);

  const isError = useMemo(() => {
    if (isAdvertiser) return isAdvError;
    if (!isAdvertiser) return isPubError;
  }, [isPubError, isAdvError]);

  const {
    isActive,
    toggle,
    itemsToShow,
    handleSaveSelectedApps,
    handleSearchOnChange,
    handleClearSearchValue,
    restore,
    flag,
    isGlobal,
  } = useToggleState(
    selectedApplications,
    foundedApps,
    setSearch,
    setSelectedApplications,
    search,
    setIsOpen,
  );

  useEffect(() => {
    if (search.length > 3 && debouncedValue.length > 3) {
      !isAdvertiser && publisherTrigger(search);
      isAdvertiser && advertiserTrigger(search);
    }
  }, [debouncedValue]);

  const handleClose = () => {
    restore();
    dispatch(searchExcludeAPI.util.resetApiState());
    setSearch('');
    setIsOpen(false);
  };

  return (
    <>
      <Buttons.SecondaryNormal
        style={{ width: '100%' }}
        id='categories'
        onClick={() => setIsOpen(true)}
      >
        Choose Applications
      </Buttons.SecondaryNormal>
      {isOpen && (
        <ModalBase>
          <S.Box>
            <ModalHeader title={title} close={handleClose} />
            <S.Content>
              <ChooseBase.OneSection
                searchValue={search}
                searchOnChange={handleSearchOnChange}
                handleClearSearchValue={handleClearSearchValue}
                dontShowHeader
                title='Search App'
              >
                {isFetching && (
                  <S.LoaderWrap>
                    <Loader.Local delay={0.01} />
                  </S.LoaderWrap>
                )}
                {isError && !isFetching > 0 && search.length > 3 && <ErrorComponent />}
                {!isFetching && (
                  <ContentWrapp
                    foundedItems={itemsToShow()}
                    search={search}
                    title='Use the search to choose a specific application.'
                  >
                    <Content
                      foundedApps={itemsToShow()}
                      search={search}
                      isActive={isActive}
                      toggle={toggle}
                      isGlobal={isGlobal}
                    />
                  </ContentWrapp>
                )}
              </ChooseBase.OneSection>
            </S.Content>
            <ModalFooter
              saveButtonText='Apply'
              saveButtonOnClick={handleSaveSelectedApps}
              close={handleClose}
              saveButtonDisabled={flag.current}
            />
          </S.Box>
        </ModalBase>
      )}
    </>
  );
};

export default ChooseApplications;
